import { debounce } from "lodash";
import * as Sentry from "@sentry/react";
import { SentryBrowserClient } from "au.com.easil.metrics";
import Logger from "lib/logger";
import PATHS from "routes/paths";

const PAGE_VIEW_EVENT = "page_view";

const userTracker = {};

/* the most recently identified user; lets us attach team_id / user id to every
   event */
let identifiedUser = null;

let metricsClient = null;
const getMetricsClient = () => {
  if (!metricsClient) {
    /* Pass only the Sentry APIs the metrics client uses, not the whole
       namespace. Handing the `import * as Sentry` object to another module
       defeats tree-shaking — the bundler must then retain every @sentry/react
       export (replay, replay-canvas, feedback), inflating the eager entry
       chunk. These named reads keep replay/feedback shakeable. */
    metricsClient = new SentryBrowserClient({
      metrics: Sentry.metrics,
      setUser: Sentry.setUser,
      setContext: Sentry.setContext,
      flush: Sentry.flush
    });
  }
  return metricsClient;
};

const safelyCall = function (f) {
  return function (...args) {
    try {
      f.apply(this, args);
    } catch (error) {
      /* in case 3rd party throws something, we log and keep going */
      Logger.error(error);
      Logger.warn("3rd Party metrics operation failed, logging and continuing");
    }
  };
};

function identify({
  id,
  email,
  name,
  country,
  roles,
  createdAt: created_at,
  teamPlan: subscription_plan,
  teamName: team_name,
  teamId: team_id
}) {
  identifiedUser = { id, team_id };

  getMetricsClient().identify(id, {
    email,
    roles,
    name,
    country,
    created_at,
    subscription_plan,
    team_id,
    team_name
  });
}
userTracker.identify = safelyCall(identify);

const getPageName = path => {
  if (PATHS.isEditorPath(path)) {
    return "Editor";
  }
  if (PATHS.isOrderPath(path)) {
    return "Orders";
  }
  if (PATHS.isPurchaseCollection(path)) {
    return "Purchase Collection";
  }
  if (PATHS.isInvitationPath(path)) {
    return "Invitation";
  }
  return path;
};

function page({ path }) {
  const pageName = getPageName(path);

  const userId = identifiedUser ? identifiedUser.id : null;
  getMetricsClient().track(userId, PAGE_VIEW_EVENT, { page_name: pageName });
}
/* the debounce here avoid tracking redirects */
userTracker.page = debounce(safelyCall(page), 300);

function getTeamId() {
  return identifiedUser ? identifiedUser.team_id : undefined;
}

function track({ event, properties }) {
  const propertiesWithTeamId = {
    ...properties,
    team_id: getTeamId()
  };

  const userId = identifiedUser ? identifiedUser.id : null;
  getMetricsClient().track(userId, event, propertiesWithTeamId);
}
userTracker.track = safelyCall(track);

userTracker.start = user => {
  userTracker.identify(user);
};

userTracker.events = {
  addGroup: "add_group",
  addPage: "add_page",
  categoryView: "category_view",
  collectionProductAdded: "collection_product_added",
  copyDesign: "copy_design",
  designCreated: "design_created",
  designDownload: "design_download",
  designMerge: "design_merge",
  designRender: "design_render",
  editorCrash: "editor_crash",
  mediaFavourited: "media_favourited",
  mediaUpload: "media_upload",
  pageView: PAGE_VIEW_EVENT,
  purchaseCollectionView: "purchase_collection_view",
  resizeDesign: "resize_design",
  teamMediaUpload: "team_media_upload",
  textMask: "text_mask",
  userMediaUpload: "user_media_upload"
};

export default userTracker;
